import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Download,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGE_ID = "deeper_reactivation";

const inclusions = [
  {
    eyebrow: "01",
    title: "Four named Reactivation Exercises",
    body:
      "Draw Like a One-Year-Old, Talk Out Loud, Intuitive Word Activation, and Stream Writing. Each with the neuroscience and the soul-layer behind why it works.",
  },
  {
    eyebrow: "02",
    title: "Eighteen deeper reflection questions",
    body:
      "Slow, honest prompts that move you past the surface into what your creativity is actually asking for. Read them once, sit with them, return to them.",
  },
  {
    eyebrow: "03",
    title: "The Mindset Shift: soul versus ego",
    body:
      "The chapter that picks up where the free reset ends. How to recognise which voice you are creating from, and how to choose the one that expands instead of contracts.",
  },
  {
    eyebrow: "04",
    title: "A free 20-minute Reactivation Session",
    body:
      "Included at the end of the guide. A real conversation with Maria-Katharina to map where you feel blocked and what is ready to move next. No pressure to work together.",
  },
];

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function TheDeeperReactivation() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const initialFirstName = (location.state?.firstName || query.get("first_name") || "").trim();
  const initialEmail = (location.state?.email || query.get("email") || "").trim();
  const cancelled = query.get("cancelled") === "1";

  const [firstName, setFirstName] = useState(initialFirstName);
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "The Deeper Reactivation · Awakened Artist Academy";
    const desc =
      "An extended companion to the free Creative Reactivation Formula. A small optional next step. $5.";
    const setMeta = (sel, attr, val) => {
      const el = document.head.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", "The Deeper Reactivation · Awakened Artist Academy");
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:description"]', "content", desc);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const startCheckout = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: PACKAGE_ID,
        origin_url: origin,
        email: email || null,
        first_name: firstName || null,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      setLoading(false);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Couldn't start checkout");
    }
  };

  const skipToWelcome = () => {
    navigate("/welcome", { state: { firstName: firstName || null, skippedUpgrade: true } });
  };

  return (
    <div data-testid="deeper-page" className="min-h-screen bg-paper text-[#1A1A1A] flex flex-col">
      <Navbar />

      <main className="flex-1">
        <Hero
          firstName={firstName}
          email={email}
          setFirstName={setFirstName}
          setEmail={setEmail}
          loading={loading}
          error={error}
          cancelled={cancelled}
          onStart={startCheckout}
          onSkip={skipToWelcome}
        />
        <Inside onStart={startCheckout} loading={loading} />
        <Honest onSkip={skipToWelcome} />
      </main>

      <Footer />
    </div>
  );
}

function Hero({
  firstName,
  email,
  setFirstName,
  setEmail,
  loading,
  error,
  cancelled,
  onStart,
  onSkip,
}) {
  return (
    <section
      data-testid="deeper-hero"
      className="relative overflow-hidden border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] hover:text-[#1FA5B8] font-body transition-colors"
        >
          <ArrowRight size={14} strokeWidth={1.5} className="rotate-180" />
          Back to the Academy
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10 items-start">
          {/* Left column — the gentle offer */}
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#1A1A1A]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                A small optional next step
              </span>
            </div>

            <h1
              data-testid="deeper-headline"
              className="font-display text-[#1A1A1A] tracking-tighter leading-[0.98] text-[clamp(2.75rem,6vw,5rem)] font-medium"
            >
              The <span className="italic font-light">Deeper</span> Reactivation.
            </h1>

            <p className="mt-6 font-display italic text-[#1FA5B8] text-[clamp(1.3rem,2.4vw,1.85rem)] leading-tight max-w-2xl">
              An extended companion to the free guide you just received.
            </p>

            <div className="mt-8 max-w-xl font-body text-[#1A1A1A] text-lg leading-[1.75] space-y-5">
              {firstName ? (
                <p>
                  <span className="italic">{firstName},</span> the free guide
                  is on its way to your inbox. If today felt like a small
                  doorway, here is a quiet next room. It is not necessary. It
                  just goes a little further.
                </p>
              ) : (
                <p>
                  Your free guide is on its way to your inbox. If today felt
                  like a small doorway, here is a quiet next room. It is not
                  necessary. It just goes a little further.
                </p>
              )}
              <p className="text-[#5C5C5C]">
                A short PDF, not a long one. Honest, slow, kept small on
                purpose. Five dollars. You will only see this offer here.
              </p>
            </div>

            <form onSubmit={onStart} className="mt-10 max-w-xl">
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="deeper-first-name"
                    className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#5C5C5C]"
                  >
                    First name (optional)
                  </label>
                  <input
                    id="deeper-first-name"
                    data-testid="deeper-first-name-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Mira"
                    className="bg-transparent text-[#1A1A1A] placeholder:text-[#5C5C5C]/70 border-0 border-b border-[#1A1A1A]/40 rounded-none px-0 h-12 text-base focus:outline-none focus:border-[#1FA5B8] w-full"
                    autoComplete="given-name"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label
                    htmlFor="deeper-email"
                    className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#5C5C5C]"
                  >
                    Email
                  </label>
                  <input
                    id="deeper-email"
                    data-testid="deeper-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@studio.com"
                    className="bg-transparent text-[#1A1A1A] placeholder:text-[#5C5C5C]/70 border-0 border-b border-[#1A1A1A]/40 rounded-none px-0 h-12 text-base focus:outline-none focus:border-[#1FA5B8] w-full"
                    autoComplete="email"
                  />
                </div>
              </div>

              {error && (
                <div
                  data-testid="deeper-error"
                  className="mt-4 font-body text-sm text-red-600"
                >
                  {error}
                </div>
              )}

              {cancelled && !error && (
                <div
                  data-testid="deeper-cancelled-note"
                  className="mt-4 font-body italic text-sm text-[#5C5C5C]"
                >
                  Checkout was cancelled. You can try again whenever you want.
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  type="submit"
                  data-testid="deeper-checkout-button"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-3 bg-[#1FA5B8] hover:bg-[#178391] disabled:opacity-60 disabled:cursor-not-allowed text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-7 transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} strokeWidth={1.6} className="animate-spin" />
                      Opening checkout
                    </>
                  ) : (
                    <>
                      Get the Deeper Reactivation  ·  $5
                      <ArrowRight size={16} strokeWidth={1.6} />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onSkip}
                  data-testid="deeper-skip-link"
                  className="font-body text-[12px] uppercase tracking-[0.22em] text-[#5C5C5C] hover:text-[#1FA5B8] transition-colors underline underline-offset-4 decoration-[#5C5C5C]/30 hover:decoration-[#1FA5B8]"
                >
                  Continue without it
                </button>
              </div>
              <p className="mt-4 font-body text-[12px] text-[#5C5C5C]/80 leading-relaxed">
                Secure payment via Stripe. One-time. No subscription. Delivered as a PDF.
              </p>
            </form>
          </div>

          {/* Right side card — the offering, minimal */}
          <aside
            data-testid="deeper-side-card"
            className="col-span-12 md:col-span-4 md:col-start-9 border border-[#E5E0D8] bg-[#FFFFFF] p-7 self-start"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
              The companion piece
            </div>
            <div className="mt-4 font-display text-[#1A1A1A] text-[28px] leading-[1.15] tracking-tight">
              The <span className="italic font-light">Deeper</span> Reactivation
            </div>
            <p className="mt-3 font-body text-sm text-[#5C5C5C] leading-relaxed">
              A short PDF that picks up exactly where the free guide ends.
              Optional, gentle, kept small on purpose.
            </p>

            <div className="mt-6 hairline" />

            <ul className="mt-5 space-y-3 font-body text-[13px] text-[#1A1A1A] leading-relaxed">
              <li className="flex items-start gap-2">
                <Check size={14} strokeWidth={2} className="mt-1 text-[#1FA5B8] flex-shrink-0" />
                4 named exercises
              </li>
              <li className="flex items-start gap-2">
                <Check size={14} strokeWidth={2} className="mt-1 text-[#1FA5B8] flex-shrink-0" />
                18 reflection questions
              </li>
              <li className="flex items-start gap-2">
                <Check size={14} strokeWidth={2} className="mt-1 text-[#1FA5B8] flex-shrink-0" />
                The soul-versus-ego mindset shift
              </li>
              <li className="flex items-start gap-2">
                <Check size={14} strokeWidth={2} className="mt-1 text-[#1FA5B8] flex-shrink-0" />
                A free 20-minute call with Maria
              </li>
            </ul>

            <div className="mt-7 hairline" />

            <div className="mt-5 flex items-baseline justify-between">
              <div className="font-display text-[#1A1A1A] text-[36px] leading-none tracking-tight">
                $5
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                One-time
              </div>
            </div>
            <p className="mt-3 font-body text-[11px] uppercase tracking-[0.22em] text-[#5C5C5C]">
              Instant download  ·  Delivered to your inbox
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Inside({ onStart, loading }) {
  return (
    <section
      data-testid="deeper-inside"
      className="bg-[#FFFFFF] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              What lives inside
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.05] tracking-tight text-[clamp(2.25rem,4.6vw,3.5rem)] font-medium">
              The full <span className="italic font-light">Creative Reactivation Formula</span>, picking up where the free reset ends.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="font-body text-[#5C5C5C] text-base md:text-lg leading-relaxed">
              A nine-page PDF guide. Not a book. Not a course. The full
              Reactivation Formula with the four exercises, the deeper
              reflection questions, the mindset chapter, and a real
              twenty-minute call when you're ready.
            </p>
          </div>
        </div>

        <ol className="grid grid-cols-12 gap-x-8 gap-y-12">
          {inclusions.map((p, i) => (
            <li
              key={p.eyebrow}
              data-testid={`deeper-inside-${i + 1}`}
              className="col-span-12 md:col-span-6 border-t border-[#E5E0D8] pt-6"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-display text-[26px] leading-none tracking-tight text-[#1FA5B8]">
                  {p.eyebrow}
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body">
                  Inside
                </span>
              </div>
              <h3 className="mt-5 font-display text-[#1A1A1A] text-[22px] md:text-[24px] leading-[1.25] tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 font-body text-[#5C5C5C] leading-relaxed">
                {p.body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-16 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <button
            type="button"
            onClick={onStart}
            disabled={loading}
            data-testid="deeper-inside-cta"
            className="inline-flex items-center justify-center gap-3 bg-[#1FA5B8] hover:bg-[#178391] disabled:opacity-60 text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-7 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={16} strokeWidth={1.6} className="animate-spin" />
                Opening checkout
              </>
            ) : (
              <>
                Get the Deeper Reactivation  ·  $5
                <ArrowRight size={16} strokeWidth={1.6} />
              </>
            )}
          </button>
          <p className="font-body text-[12px] text-[#5C5C5C]">
            One-time, $5. Stripe checkout. Instant PDF.
          </p>
        </div>
      </div>
    </section>
  );
}

function Honest({ onSkip }) {
  return (
    <section
      data-testid="deeper-honest"
      className="bg-[#F7F5F2] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              A small note
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.05] tracking-tight text-[clamp(1.85rem,3.2vw,2.5rem)] font-medium">
              You do not have to take this{" "}
              <span className="italic font-light">step</span>.
            </h2>
            <div className="mt-5 max-w-2xl font-body text-[#1A1A1A] text-[17px] leading-[1.75] space-y-4">
              <p>
                The free guide is whole on its own. The Deeper Reactivation
                is for the days you feel ready to go a little further, and
                only then. If today is not that day, please continue without
                it. The Academy door stays open.
              </p>
              <p className="text-[#5C5C5C]">
                If you change your mind later, you can come back to this
                page from any email I send.
              </p>
            </div>

            <button
              type="button"
              onClick={onSkip}
              data-testid="deeper-honest-skip"
              className="mt-8 inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.22em] text-[#1A1A1A] hover:text-[#1FA5B8] transition-colors"
            >
              Continue without it
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </button>
          </div>

          <aside className="col-span-12 md:col-span-4 md:col-start-9 border border-[#E5E0D8] bg-[#FFFFFF] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
              Delivery
            </div>
            <ul className="mt-4 space-y-3 font-body text-sm text-[#1A1A1A] leading-relaxed">
              <li className="flex items-start gap-2">
                <Download size={14} strokeWidth={1.6} className="mt-1 text-[#1FA5B8] flex-shrink-0" />
                Instant PDF download after checkout
              </li>
              <li className="flex items-start gap-2">
                <Download size={14} strokeWidth={1.6} className="mt-1 text-[#1FA5B8] flex-shrink-0" />
                A copy also lands in your inbox
              </li>
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
}
